import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { UserMovieDataProvider } from '../../contexts/UserMovieDataContext';
import { UserWatchlistDataProvider } from '../../contexts/UserWatchlistDataContext';
import { UserFollowProvider } from '../../contexts/UserFollowContext';
import { NotificationProvider } from '../../contexts/NotificationContext';
import { UserProfileCacheProvider } from '../../contexts/UserProfileCacheContext';
import { ToastProvider } from '../utils/ToastProvider';
import { ScrollPositionProvider } from '../../contexts/ScrollPositionContext';
import { PWAInstallProvider } from '../../contexts/PWAInstallContext';
import { MovieRatingProvider } from '../../contexts/MovieRatingContext';
import { HighlightProvider } from '../../contexts/HighlightContext';

interface ContextProvidersProps {
  children: React.ReactNode;
}

export const ContextProviders: React.FC<ContextProvidersProps> = ({ children }) => {
  const { user, isFirebaseReady, isRoleLoading } = useAuth();

  // Don't expose the user to data contexts until the role is confirmed —
  // prevents Firestore queries firing for pending/unauthorized users
  const resolvedUser = isRoleLoading ? null : user;

  // Key only on user identity — providers receive isFirebaseReady as a prop and
  // guard their own queries internally. Keying on isFirebaseReady caused two full
  // unmount/remount cycles on every cold open (false→true before user resolves).
  const contextKey = `user-${resolvedUser?.uid || 'none'}`;

  return (
    <PWAInstallProvider>
      <ScrollPositionProvider>
        <HighlightProvider>
          <UserProfileCacheProvider>
            <UserMovieDataProvider user={resolvedUser} isFirebaseReady={isFirebaseReady} key={`movie-${contextKey}`}>
              <UserWatchlistDataProvider user={resolvedUser} isFirebaseReady={isFirebaseReady} key={`watchlist-${contextKey}`}>
                <UserFollowProvider user={resolvedUser} key={`follow-${contextKey}`}>
                  <NotificationProvider user={resolvedUser} key={`notification-${contextKey}`}>
                    <MovieRatingProvider>
                      <ToastProvider>
                        {children}
                      </ToastProvider>
                    </MovieRatingProvider>
                  </NotificationProvider>
                </UserFollowProvider>
              </UserWatchlistDataProvider>
            </UserMovieDataProvider>
          </UserProfileCacheProvider>
        </HighlightProvider>
      </ScrollPositionProvider>
    </PWAInstallProvider>
  );
};
